import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { quotaService } from '../../services/quotaService';
import { User, Mail, TrendingUp, Zap } from 'lucide-react';

export const UserProfile = () => {
    const { user } = useAuth();
    const [quotaData, setQuotaData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchQuotaData();
        // Refresh quota every 5 seconds
        const interval = setInterval(fetchQuotaData, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchQuotaData = async () => {
        try {
            const data = await quotaService.getQuotaStatus();
            setQuotaData(data);
        } catch (error) {
            console.error('Failed to fetch quota:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!user) return null;

    // FIX (MISSING-001 / FRONTEND-BUG-006): Real quota from backend
    const dailyUsed = quotaData?.requests_today || 0;
    const dailyLimit = quotaData?.daily_limit || 1000;
    const dailyPercentage = Math.round((dailyUsed / dailyLimit) * 100);
    const minuteLimit = quotaData?.per_minute_limit || 15;

    return (
        <div className="card space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-semibold">Personal Information</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Your account details and quota usage
                    </p>
                </div>
                <button
                    onClick={fetchQuotaData}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                    title="Refresh Data"
                >
                    <TrendingUp className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-glow" style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>
                        {user.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <User className="w-4 h-4" />
                            <span className="font-medium">{user.username}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <Mail className="w-4 h-4" />
                            <span>{user.email}</span>
                        </div>
                    </div>
                </div>

                {/* Real quota display */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Daily Quota</div>
                        </div>
                        {loading ? (
                            <div className="text-2xl font-bold text-blue-600">...</div>
                        ) : (
                            <>
                                <div className="text-2xl font-bold text-blue-600">
                                    {dailyUsed} / {dailyLimit}
                                </div>
                                <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 transition-all duration-300"
                                        style={{ width: `${Math.min(dailyPercentage, 100)}%` }}
                                    />
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {dailyPercentage}% used today
                                </div>
                            </>
                        )}
                    </div>
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-4 h-4 text-green-600" />
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Rate Limit</div>
                        </div>
                        <div className="text-2xl font-bold text-green-600">{minuteLimit} / min</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Requests per minute
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
